import { Copy } from "lucide-react";
import type { ReactNode } from "react";

import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { cn } from "@/shared/lib/utils";

import { ASSET_GLYPHS } from "../../constants/cryptoSwaps.constants";
import { useCopyValue } from "../../hooks/useCopyValue";
import { UsdtTokenIcon } from "./UsdtTokenIcon";

export interface AssetAvatarProps {
  asset: string;
  kind?: "FIAT" | "ONCHAIN";
  size?: "xs" | "sm" | "md";
  className?: string;
}

const AVATAR_PX = { xs: 16, sm: 24, md: 32 } as const;

const AVATAR_CLASS = {
  xs: "size-4 text-[8px]",
  sm: "size-6 text-[10px]",
  md: "size-8 text-[13px]",
} as const;

/** The round asset mark next to an amount: the real token logo for USDT, a neutral glyph badge otherwise. */
export function AssetAvatar({ asset, kind = "FIAT", size = "md", className }: AssetAvatarProps) {
  if (asset === "USDT") {
    return <UsdtTokenIcon size={AVATAR_PX[size]} className={cn("shrink-0", className)} />;
  }
  const glyph = ASSET_GLYPHS[asset] ?? asset.slice(0, 1);
  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full font-semibold",
        AVATAR_CLASS[size],
        kind === "ONCHAIN" ? "bg-primary/10 text-primary" : "bg-muted text-foreground",
        className,
      )}
    >
      {glyph}
    </span>
  );
}

export interface CopyValueButtonProps {
  value: string;
  /** Interpolated into the toast and into the accessible name — never just "Copy". */
  fieldLabel: string;
  size?: "xs" | "sm" | "default";
  className?: string;
}

/** Icon-only copy affordance. The value it copies is always rendered as selectable text beside it. */
export function CopyValueButton({ value, fieldLabel, size = "sm", className }: CopyValueButtonProps) {
  const copy = useCopyValue();
  return (
    <ColocaIconButton
      type="button"
      variant="ghost"
      size={size}
      icon={Copy}
      aria-label={fieldLabel}
      className={cn("shrink-0 text-muted-foreground", className)}
      onClick={() => void copy(value, fieldLabel)}
    />
  );
}

export interface DetailRowProps {
  label: ReactNode;
  children: ReactNode;
  /** Right-align the value — the summary card's convention, so figures line up on the decimal. */
  align?: "start" | "end";
  className?: string;
}

/** One line of a summary: a term and its value, as a real description list. */
export function DetailRow({ label, children, align = "end", className }: DetailRowProps) {
  return (
    <>
      <dt className={cn("text-sm text-muted-foreground", className)}>{label}</dt>
      <dd className={cn("m-0 text-sm text-foreground", align === "end" ? "text-right" : "text-left")}>
        {children}
      </dd>
    </>
  );
}

export interface MonoValueProps {
  children: ReactNode;
  /** Long values (an address, a QR payload) wrap mid-token instead of overflowing their box. */
  breakAll?: boolean;
  className?: string;
}

/** Tabular, selectable text for anything the user may need to read character by character. */
export function MonoValue({ children, breakAll, className }: MonoValueProps) {
  return <span className={cn("font-mono tabular-nums", breakAll && "break-all", className)}>{children}</span>;
}
